import { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import SafeImage from './SafeImage';
import { CONTACT_VISUAL_IMAGE, IMAGE_FALLBACK_CHAIN } from '../config/siteImages';
import { COMPANY_EMAIL, COMPANY_NAME } from '../config/siteInfo';

const ICONS = {
  phone: (
    <>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.7 12.3 19.79 19.79 0 0 1 1.64 3.6 2 2 0 0 1 3.62 1.4h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.03a16 16 0 0 0 6.06 6.06l1.96-1.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </>
  ),
  email: (
    <>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </>
  ),
  location: (
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  hours: (
    <>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </>
  ),
};

const serviceOptions = [
  'Steel Fabrication',
  'Structural Steel',
  'Architectural Landscape Products',
  'Staircases',
  'Mobile Welding',
  'Steel Supply',
  'Steel Beams',
  'Steel Columns',
  'Design',
  'Steel Connections',
  'Steel Sections',
  'Site Surveys',
  'Gates',
  'Balustrades',
  'Mezzanines',
  'Railing',
  'Welding',
  'Other',
];

const highlights = [
  'Itemised quotations — no hidden extras',
  'Workshop and site teams under one roof',
  'BS EN 1090–compliant fabrication',
  'Nationwide delivery and installation available',
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.65, delay, ease: [0.25, 0.1, 0.25, 1] },
});

const initialForm = {
  name: '',
  phone: '',
  email: '',
  service: '',
  message: '',
};

export default function Contact({ siteSettings }) {
  const phone = siteSettings?.phone || '+44 (0) 1234 567 890';
  const email = siteSettings?.contactEmail || COMPANY_EMAIL;
  const location = siteSettings?.location || 'Midlands, United Kingdom';

  const contactInfo = [
    { label: 'Phone', value: phone, icon: ICONS.phone },
    { label: 'Email', value: email, icon: ICONS.email },
    { label: 'Location', value: location, icon: ICONS.location },
    { label: 'Hours', value: 'Mon–Fri: 7:30am – 5:00pm', icon: ICONS.hours },
  ];

  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendError, setSendError] = useState('');

  const handleChange = (e) => {
    if (sendError) setSendError('');
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSendError('');
    setSubmitting(true);

    const messageBody = form.service
      ? `${form.message}\n\n—\nService required: ${form.service}`
      : form.message;

    emailjs
      .send(
        'service_x7iypub',
        'template_44ovpoz',
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: messageBody,
        },
        'ca7ERZvyx_GQPut0K'
      )
      .then(() => {
        setSent(true);
        setForm(initialForm);
      })
      .catch(() => {
        setSendError(
          'Something went wrong while sending your enquiry. Please try again in a moment or contact us by phone or email.'
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <section className="contact" id="contact">
      <div className="container">
        <div className="contact-inner">
          <div className="contact-info">
            <motion.div className="eyebrow" {...fadeUp(0)}>
              Contact
            </motion.div>
            <motion.h2 className="section-title" {...fadeUp(0.08)}>
              Request a quote
            </motion.h2>
            <motion.p className="section-sub" {...fadeUp(0.14)}>
              Share your drawings, dimensions, and timeline — we will respond with a clear,
              itemised quotation, usually within one business day. Prefer to talk it
              through? Call us directly.
            </motion.p>

            <motion.div className="contact-highlights" {...fadeUp(0.2)}>
              <h3>What you can expect</h3>
              <ul>
                {highlights.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="contact-items"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
              }}
            >
              {contactInfo.map((c) => (
                <motion.div
                  key={c.label}
                  className="ci"
                  variants={{
                    hidden: { opacity: 0, x: -12 },
                    visible: { opacity: 1, x: 0, transition: { duration: 0.45 } },
                  }}
                >
                  <div className="ci-icon">
                    <svg viewBox="0 0 24 24">{c.icon}</svg>
                  </div>
                  <div>
                    <div className="ci-label">{c.label}</div>
                    <div className="ci-val">{c.value}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/*
            Side image: CONTACT_VISUAL_IMAGE in src/config/siteImages.js
          */}
          <aside className="contact-visual" aria-label="Steel fabrication imagery">
            <div className="contact-visual-card">
              <SafeImage
                src={CONTACT_VISUAL_IMAGE}
                fallbackSrcs={IMAGE_FALLBACK_CHAIN}
                alt="Steel fabrication and project delivery"
                wrapperClassName="contact-visual-img-wrap"
                imgClassName="contact-visual-img"
              />
              <div className="contact-visual-caption">
                <span className="contact-visual-caption-kicker">{COMPANY_NAME}</span>
                <span className="contact-visual-caption-text">
                  Workshop precision, site-ready delivery.
                </span>
              </div>
            </div>
          </aside>

          <motion.div
            className="contact-form-wrap"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {sent ? (
              <div className="form-success">
                <svg viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h4>Thank you</h4>
                <p>Your enquiry has been received. We will be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <h3 className="form-heading">Project details</h3>

                {sendError ? (
                  <div className="form-error" role="alert">
                    {sendError}
                  </div>
                ) : null}

                <div className="form-row">
                  <div className="fg">
                    <label htmlFor="contact-name">Name *</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      placeholder="Your name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div className="fg">
                    <label htmlFor="contact-phone">Phone *</label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      placeholder="+44 …"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                    />
                  </div>
                </div>

                <div className="fg">
                  <label htmlFor="contact-email">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="fg">
                  <label htmlFor="contact-service">Service required *</label>
                  <select
                    id="contact-service"
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select a service…
                    </option>
                    {serviceOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="fg">
                  <label htmlFor="contact-message">Message *</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    placeholder="Tell us about your project — sizes, quantities, site location, and any deadlines."
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                  />
                </div>

                <button type="submit" className="form-submit-btn" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Submit enquiry'}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
                  </svg>
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
